/**
 * Lista de produtos (core.products) compartilhada — seletor do topo, filtros e páginas por produto.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode
} from 'react';
import { apiService } from '@/services/api';

export type ProductSummary = {
  productId: string;
  displayName?: string;
  avatarUrl?: string;
  status?: string;
  type?: string;
  productUrl?: string;
  /** Produto gratuito: sem período de teste e sem cobrança (billing sempre desligado). */
  isFree?: boolean;
  urls?: { appApi?: string; appFrontend?: string; appApiSecretConfigured?: boolean };
  integrations?: {
    billingEnabled?: boolean;
    pricingSchema?: string;
  };
};

interface ProductsContextType {
  products: ProductSummary[];
  productsById: Map<string, ProductSummary>;
  loading: boolean;
  refreshProducts: () => Promise<void>;
}

const ProductsContext = createContext<ProductsContextType | undefined>(undefined);

export const ProductsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.listProducts();
      const items = (res.data?.items || []) as ProductSummary[];
      setProducts(items.filter(p => p.productId));
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshProducts().catch(() => undefined);
  }, [refreshProducts]);

  const productsById = useMemo(() => {
    const map = new Map<string, ProductSummary>();
    for (const p of products) {
      map.set(p.productId, p);
    }
    return map;
  }, [products]);

  return (
    <ProductsContext.Provider value={{ products, productsById, loading, refreshProducts }}>
      {children}
    </ProductsContext.Provider>
  );
};

export const useProducts = (): ProductsContextType => {
  const ctx = useContext(ProductsContext);
  if (!ctx) {
    throw new Error('useProducts must be used within ProductsProvider');
  }
  return ctx;
};
